import type { WalkOnSong } from '@/core/schemas';

/**
 * Turns a stored song into an embeddable player URL with start/end and
 * autoplay, currently for YouTube links.
 *
 * The snippet length is capped defensively at `maxDurationSeconds` here too:
 * the registration form already enforces it, but the beamer plays whatever is
 * stored, and a legacy or hand-edited document must not be able to play a
 * three-minute clip on the hall's speakers.
 */
export interface PlayableSnippet {
  readonly embedUrl: string;
  readonly durationSeconds: number;
}

const YOUTUBE_HOSTS = new Set(['youtube.com', 'www.youtube.com', 'youtu.be', 'm.youtube.com']);

function youTubeId(url: URL): string | null {
  if (url.hostname === 'youtu.be') {
    return url.pathname.slice(1) || null;
  }
  if (url.pathname === '/watch') {
    return url.searchParams.get('v');
  }
  if (url.pathname.startsWith('/embed/')) {
    return url.pathname.slice('/embed/'.length) || null;
  }
  return null;
}

export function toPlayableSnippet(
  song: WalkOnSong,
  maxDurationSeconds: number,
): PlayableSnippet | null {
  if (song.sourceUrl === undefined) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(song.sourceUrl);
  } catch {
    return null;
  }

  if (!YOUTUBE_HOSTS.has(url.hostname)) {
    return null;
  }

  const id = youTubeId(url);
  if (id === null) {
    return null;
  }

  const start = Math.max(0, song.startSeconds);
  const cappedEnd = Math.min(song.endSeconds, start + maxDurationSeconds);
  const durationSeconds = Math.max(0, cappedEnd - start);

  const params = new URLSearchParams({
    autoplay: '1',
    start: String(start),
    end: String(cappedEnd),
    controls: '0',
    rel: '0',
    modestbranding: '1',
  });

  return {
    embedUrl: `https://www.youtube-nocookie.com/embed/${id}?${params.toString()}`,
    durationSeconds,
  };
}
